import { RegistrationCancellationList, RegistrationSession, Session } from "@/api/dtos";
import RegistrationField from "@/components/RegistrationField";
import { AdminContext } from "@/contexts/AdminContext";
import { faMinus, faPlus } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Button, Checkbox, InputAdornment, Radio, styled } from "@mui/material";
import { Numeric } from "@ngt/forms";
import { useScopedField } from "@ngt/forms-core";
import { DateTime } from "luxon";
import MuiMarkdown from "mui-markdown/dist";
import { FC, useCallback, useContext, useMemo } from "react";
import { NumberFormatValues } from "react-number-format";

interface ISessionProps {
    session: Session;
    selectedSession?: RegistrationSession;
    selectedSessionIndex?: number;
    initialSession?: RegistrationSession;
    initialSessionIndex?: number;
    selectedCancellationList?: RegistrationCancellationList;
    selectedCancellationListIndex?: number;
    initialCancellationList?: RegistrationCancellationList;
    initialCancellationListIndex?: number;
    included?: boolean;
    optional?: boolean;
    updateSelectedSessions: (session: Session, selectedSession?: RegistrationSession, selectedSessionIndex?: number, included?: boolean, optional?: boolean) => void;
        updateSelectedCancellationLists: (session: Session, selectedCancellationList?: RegistrationCancellationList, selectedCancellationListIndex?: number/*, included?: boolean, optional?: boolean*/) => void;
}


const SessionBox = styled(Box)(({ theme }) => ({
    margin: theme.spacing(0, -2),
    padding: theme.spacing(2, 2)
}))

const SessionFlexBox = styled(Box)(({ theme }) => ({

    display: 'flex',
    gap: theme.spacing(3),
    alignItems: "flex-start",
    [theme.breakpoints.down('md')]: {
        gap: theme.spacing(3),
    }


}))


const SessionSelection = styled(Box)(() => ({
    flex: '1 1 auto',
    minWidth: 24,
    maxWidth: 24
}))

const SessionRadio = styled(Radio)(({theme}) => ({
    padding: 0,

    [theme.breakpoints.down('md')]: {

    }
}))

const SessionCheckbox = styled(Checkbox)(() => ({
    padding: 0,
}))

const SessionDetails = styled(Box)(() => ({
    flex: '1 1 50%'
}))

const SessionDetailsWithTime = styled(Box)(({ theme }) => ({
    [theme.breakpoints.up('md')]: {
        flex: '1 1 100%',
        display: 'flex',
        gap: theme.spacing(3),
    },
    
    
    [theme.breakpoints.down('md')]: {
        flex: '1 1 50%'
    }
}))

const SessionTitle = styled(Box)(() => ({
    flex: '1 1 50%',
}))

const SessionTopic = styled(Box)(({ theme }) => ({
    [theme.breakpoints.down('md')]: {
        ...theme.typography.caption
    }
}))

const SessionSponsor = styled(Box)(({ theme }) => ({
    [theme.breakpoints.down('md')]: {
        ...theme.typography.caption
    }
}))

const SessionCapacity = styled(Box)(({ theme }) => ({
    [theme.breakpoints.down('md')]: {
        ...theme.typography.caption
    }
}))

const SessionDateTime = styled(Box)(({ theme }) => ({
    flex: '1 1 50%',
    whiteSpace: 'pre-wrap',
    [theme.breakpoints.down('md')]: {
        ...theme.typography.caption
    }
}))


const SessionPrice = styled(Box)(({theme}) => ({

    flex: '1 1 auto',
    minWidth: 60,
    maxWidth: 60,
    //textAlign: 'right',

    [theme.breakpoints.down('md')]: {
        minWidth: 'unset',
        maxWidth: 'unset',
        display: 'flex',
        gap: theme.spacing(2),
        justifyContent: 'flex-end',
        textAlign: 'right',
    }
}))

const AdditionalAttendeesInput = styled(Box)(({theme}) => ({
    maxWidth: 300,
}))

const SessionView: FC<ISessionProps> = ({
    session,
    selectedSession,
    selectedSessionIndex,
    initialSession,
    selectedCancellationList,
    updateSelectedCancellationLists,
    selectedCancellationListIndex,
    included,
    optional,
    updateSelectedSessions
}) => {
    
    const dateAndTime = useMemo(() => {
        if (!session.startTime) {
            return undefined;
        }

        const startTime = DateTime.fromISO(session.startTime);

        const endTime = !!session.endTime ? DateTime.fromISO(session.endTime) : undefined; 
        return `${startTime.toFormat('HH:mm')}${(endTime ? ` - ${endTime.toFormat('HH:mm')}` : '')}, ${startTime.toFormat('cccc d MMMM yyyy') }`
    }, [session.startTime, session.endTime]);

    

    const costDisplay = useMemo(() => {

        let basePrice = session.price ?? 0;
        
        if (included) {
            basePrice = 0;
        }

        let numberOfAdditional = session.additionalAttendees ? selectedSession?.numberOfAttendees ?? 0 : 0;
        let additionalPrice = session.additionalPrice ?? 0;

        const totalPrice = basePrice + (numberOfAdditional * additionalPrice);

        if (!totalPrice) {
            return 'Free';
        }

        return `$${totalPrice}`;
    }, [included, session.price, session.additionalPrice, session.additionalAttendees, selectedSession?.numberOfAttendees])

    const isAdmin = useContext(AdminContext);

    const calculated = useMemo(() => {
        let calculatedAttendees = session.attendees ?? 0;

        if (initialSession) {
            calculatedAttendees -= 1;
            if (initialSession.numberOfAttendees) {
                calculatedAttendees -= initialSession.numberOfAttendees;
            }
        }        

        if (selectedSession) {
            calculatedAttendees += 1;

            if (selectedSession.numberOfAttendees) {
                calculatedAttendees += selectedSession.numberOfAttendees;
            }
        }

        return { atCapacity: session.attendeeCap ? calculatedAttendees > session.attendeeCap : false,  attendees: calculatedAttendees }; 
    }, [session.attendees, session.attendeeCap, initialSession, selectedSession, selectedSession?.numberOfAttendees])

    const { context: { setValue: setNumberOfAttendees } } = useScopedField<number>(
        selectedSessionIndex !== undefined ? `sessions[${selectedSessionIndex}].numberOfAttendees` : 'dummy.path', 
        {}
    );

    const setAttendeesPlus = useCallback(() => {
        if (selectedSessionIndex === undefined) return;
        
        setNumberOfAttendees(current => {

            let maxAdditionalAttendies = isAdmin ? 100 : 10;
            let spotsFree = null;

            if (!isAdmin)
            {
                if (session.attendeeCap){
                    if (session.attendees) {
                        spotsFree = session.attendeeCap - (session.attendees + 1); // + 1 is the selected session as additional does not take into account the person going for max count
                    }
                    else {
                        spotsFree = session.attendeeCap;
                    }
                }
                if (spotsFree && spotsFree < maxAdditionalAttendies){
                    maxAdditionalAttendies = spotsFree;
                }
            }

            const newValue = (current ?? 0) + 1;

            if (newValue <= maxAdditionalAttendies) {
                // here
                return newValue;
            }
            // here
            return maxAdditionalAttendies;
        }, true, true, true, false);

    }, [setNumberOfAttendees, session.attendeeCap, session.attendees, isAdmin, selectedSessionIndex]); 




    const setAttendeesMinus = useCallback(() => {
        if (!selectedSession || selectedSessionIndex === undefined) return;

        setNumberOfAttendees(current => {
            const newValue = (current ?? 0) - 1;
            return newValue < 0 ? 0 : newValue;
        }, true, true, true, false);

    }, [setNumberOfAttendees, selectedSession, selectedSessionIndex]);


    return (
        <SessionBox>
            <SessionFlexBox>
                <SessionSelection>
                    {included && optional && (
                        <SessionRadio
                            disabled={session.atCapacity && !isAdmin}
                            checked={!!selectedSession}
                            onClick={
                                selectedSession ? 
                                    () => {
                                        setNumberOfAttendees(undefined, true, true, true, false)
                                        updateSelectedSessions(session, selectedSession, selectedSessionIndex, false, optional)
                                    } : 
                                    undefined
                            }
                            onChange={() => {
                                setNumberOfAttendees(undefined, true, true, true, false)
                                updateSelectedSessions(session, selectedSession, selectedSessionIndex, included, optional)
                            }}
                        />
                    )}
                    {
                        !included && !optional && (
                            <SessionCheckbox
                                disabled={session.atCapacity && !isAdmin}
                                checked={!!selectedSession}
                                onChange={() => {
                                    updateSelectedSessions(session, selectedSession, selectedSessionIndex, included, optional)
                                }}
                            />
                        )
                    }
                </SessionSelection>

                <SessionDetailsWithTime>
                    <SessionDetails>
                        <SessionTitle>
                            {session.name}
                        </SessionTitle>
                        {
                            session.description && (
                                <SessionTopic>
                                    <strong>Topic: </strong> {session.description}
                                </SessionTopic>
                            )
                        }
                        {
                            session.sponsor && (
                                <SessionSponsor>
                                    <strong>Sponsor: </strong> {session.sponsor}
                                </SessionSponsor>
                            )
                        }
                        {
                            session.speaker && (
                                <SessionSponsor>
                                    <strong>Speaker: </strong> {session.speaker}
                                </SessionSponsor>
                            )
                        }
                        {
                            isAdmin && session.attendeeCap && !calculated.atCapacity && (
                                <SessionCapacity>
                                    <strong><em>{calculated.attendees} / {session.attendeeCap} attendees</em></strong>
                                </SessionCapacity>
                            )
                        }
                        {
                            calculated.atCapacity && (
                                <SessionCapacity>
                                    <strong><em>{ isAdmin && (<>{calculated.attendees} / {session.attendeeCap} </>)}Session has reached capacity</em></strong>
                                </SessionCapacity>
                            )
                        }
                    </SessionDetails>

                    <SessionDateTime>
                        {!session.dateTimeOverride ? dateAndTime : session.dateTimeOverride}
                    </SessionDateTime>
                </SessionDetailsWithTime>
                <SessionPrice>
                    {
                        !included && (!session.additionalAttendees || !selectedSession?.numberOfAttendees) && (
                            costDisplay
                        )
                    }
                </SessionPrice>
            </SessionFlexBox>
            {
                !session.atCapacity && session.attendeesBeforeCapacityMessage && (
                    <SessionFlexBox
                        sx={{
                            mt: 2
                        }}
                    >
                        <SessionSelection>
                        </SessionSelection>
                        <SessionDetails>
                            <MuiMarkdown>{session.attendeesBeforeCapacityMessage}</MuiMarkdown>
                        </SessionDetails>
                        <SessionDateTime sx={{ display: {xs: 'none', sm: 'none', md: 'none' , lg: 'block'}}}>
                        </SessionDateTime>
                        <SessionPrice sx={{ display: {xs: 'none', sm: 'none', md: 'block'}}}>
                        </SessionPrice>
                    </SessionFlexBox>
                )
            }
            {
                session.atCapacity && session.attendeesAfterCapacityMessage && (

                    <SessionFlexBox
                        sx={{
                            mt: 2
                        }}
                    >
                        <SessionSelection>
                        </SessionSelection>
                        <SessionDetails>
                            <MuiMarkdown>{session.attendeesAfterCapacityMessage}</MuiMarkdown>
                        </SessionDetails>
                
                        <SessionDateTime sx={{ display: {xs: 'none', sm: 'none', md: 'none' , lg: 'block'}}}>
                        </SessionDateTime>
                        <SessionPrice sx={{ display: {xs: 'none', sm: 'none', md: 'block'}}}>
                        </SessionPrice>
                    </SessionFlexBox>
                )
            }
            {
                session.atCapacity && session.attendeesCancellationList && (
                    <SessionFlexBox
                        sx={{
                            mt: 2
                        }}
                    >
                        <SessionSelection>
                        </SessionSelection>
                        <SessionSelection>
                            <SessionCheckbox
                                disabled={!session.atCapacity && !isAdmin}
                                checked={!!selectedCancellationList}
                                onChange={() => {
                                    updateSelectedCancellationLists(session, selectedCancellationList, selectedCancellationListIndex)
                                }}
                            />
                        </SessionSelection>

                        <SessionDetails>
                            <em>Opt into Cancellation List</em>
                        </SessionDetails>
                
                        <SessionDateTime sx={{ display: {xs: 'none', sm: 'none', md: 'none' , lg: 'block'}}}>
                        </SessionDateTime>
                        <SessionPrice sx={{ display: {xs: 'none', sm: 'none', md: 'block'}}}>
                        </SessionPrice>

                        
                    </SessionFlexBox>
                )
            }
            {
                session.additionalAttendees && (!session.attendeesCancellationList || !session.atCapacity || isAdmin) && (isAdmin || !session.attendeesAdministrationOnly) && (
                    <SessionFlexBox
                        sx={{
                            mt: 2
                        }}
                    >
                        <SessionSelection>
                        </SessionSelection>
                        <SessionDetails>
                            <AdditionalAttendeesInput>
                                <RegistrationField
                                   key={selectedSessionIndex ?? 'no-session'}
                                    size="small"
                                    label="Additional attendees"
                                    propertyPath={selectedSession && selectedSessionIndex !== undefined ? `sessions[${selectedSessionIndex}].numberOfAttendees` : `dummy.path`}
                                    disabled={!selectedSession}
                                    component={Numeric}
                                    numberFormat={{
                                        decimalScale: 0,
                                        allowNegative: false,
                                        isAllowed: (values: NumberFormatValues) => {
                                            // make it so you cant select more then the spots
                                            let maxAdditionalAttendies = isAdmin ? 100 : 10;
                                            let spotsFree = null;

                                            if (!isAdmin)
                                            {
                                                if (session.attendeeCap){
                                                    if (session.attendees) {
                                                        spotsFree = session.attendeeCap - session.attendees;
                                                    }
                                                    else {
                                                        spotsFree = session.attendeeCap;
                                                    }
                                                }
                                                if (spotsFree && spotsFree < maxAdditionalAttendies){
                                                    maxAdditionalAttendies = spotsFree;
                                                }
                                            }

                                            if (!values.floatValue) {
                                                return true;
                                            }
                                    
                                            if (values.floatValue <= maxAdditionalAttendies) {
                                                return true;
                                            }
                                    
                                            return false;
                                        }
                                    }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <Button
                                                    disabled={!selectedSession}
                                                    sx={{minWidth: 0, padding: .5, mr: 1}}
                                                    onClick={setAttendeesPlus}
                                                    color="tertiary"
                                                >
                                                    <FontAwesomeIcon icon={faPlus} fixedWidth />
                                                </Button>
                                                <Button
                                                    //variant="outlined"
                                                    disabled={!selectedSession}
                                                    sx={{minWidth: 0, padding: .5}}
                                                    onClick={setAttendeesMinus}
                                                    color="tertiary"
                                                >
                                                    <FontAwesomeIcon icon={faMinus} fixedWidth />
                                                </Button>
                                            </InputAdornment>
                                        )
                                        
                                    }}
                                />
                            </AdditionalAttendeesInput>
                        </SessionDetails>
                        <SessionDateTime>
                        </SessionDateTime>
                        <SessionPrice>
                            {
                                !!selectedSession?.numberOfAttendees && (
                                    costDisplay
                                )
                            }
                        </SessionPrice>
                    </SessionFlexBox>
                )
            }
        </SessionBox>
    );
};

export default SessionView;